import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import LocationFields from './basic-info/LocationFields';
import IncomeSlider from './basic-info/IncomeSlider';
import ProductsCheckList from './basic-info/ProductsCheckList';

interface ProfileBasicInfoProps {
  data: any;
  updateData: (data: any) => void;
  registerFlush?: (fn: () => Promise<void>) => void; // New prop for flush registration
}

const ProfileBasicInfo = ({ data, updateData, registerFlush }: ProfileBasicInfoProps) => {
  const [formData, setFormData] = useState({
    age: data.age || '',
    country: data.country || 'India',
    state: data.state || '',
    city: data.city || '',
    employmentStatus: data.employmentStatus || '',
    monthlyIncome: data.monthlyIncome || [50000],
    existingProducts: data.existingProducts || []
  });

  // Update local state when props change
  useEffect(() => {
    setFormData({
      age: data.age || '',
      country: data.country || 'India',
      state: data.state || '',
      city: data.city || '',
      employmentStatus: data.employmentStatus || '',
      monthlyIncome: data.monthlyIncome || [50000],
      existingProducts: data.existingProducts || []
    });
  }, [data]);

  // Register a flush function that updates parent state with latest local state
  useEffect(() => {
    if (registerFlush) {
      registerFlush(async () => {
        updateData(formData);
        // Wait a tick to ensure updateProfileData runs (if async)
        await new Promise((res) => setTimeout(res, 10));
      });
    }
    // No cleanup needed
    // eslint-disable-next-line
  }, [formData, registerFlush]);

  // Update parent component with form changes
  const handleChange = (field: string, value: any) => {
    console.log(`Changing ${field} to:`, value);
    const newFormData = {
      ...formData,
      [field]: value
    };
    setFormData(newFormData);
  };

  const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 0 && !value.includes('.'))) {
      handleChange('age', value);
    }
  };

  const handleProductToggle = (product: string) => {
    setFormData(prev => {
      const existingProducts = [...(prev.existingProducts || [])];
      const newProducts = existingProducts.includes(product)
        ? existingProducts.filter(p => p !== product)
        : [...existingProducts, product];
      
      const newFormData = {
        ...prev,
        existingProducts: newProducts
      };
      // Don't update parent immediately
      return newFormData;
    });
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Basic Profile Information</h2>
      
      <div className="space-y-6">
        {/* Age */}
        <div>
          <Label htmlFor="age">Age</Label>
          <Input
            id="age"
            type="text"
            placeholder="Enter your age"
            value={formData.age}
            className="bg-black border border-[#9E7C47]/30 text-white mt-1"
            onChange={handleAgeChange}
          />
        </div>
        
        {/* Location Fields */}
        <LocationFields
          country={formData.country}
          state={formData.state}
          city={formData.city}
          onFieldChange={handleChange}
        />
        
        {/* Employment Status */}
        <div>
          <Label htmlFor="employmentStatus">Employment Status</Label>
          <Select 
            value={formData.employmentStatus} 
            onValueChange={(value) => handleChange('employmentStatus', value)}
          >
            <SelectTrigger id="employmentStatus" className="bg-[#9E7C47]/5 border-[#9E7C47]/30 text-white mt-1">
              <SelectValue placeholder="Select employment status" />
            </SelectTrigger>
            <SelectContent className="bg-black border-[#9E7C47]/30">
              <SelectItem value="salaried">Salaried</SelectItem>
              <SelectItem value="self-employed">Self-employed</SelectItem>
              <SelectItem value="business-owner">Business owner</SelectItem>
              <SelectItem value="student">Student</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Monthly Income Range */}
        <IncomeSlider
          value={formData.monthlyIncome}
          onChange={(value) => handleChange('monthlyIncome', value)}
        />
        
        {/* Existing Financial Products */}
        <ProductsCheckList
          selectedProducts={formData.existingProducts}
          onProductToggle={handleProductToggle}
        />
      </div>
    </div>
  );
};

export default ProfileBasicInfo;
